import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useForm, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import { FIELD_KEYS } from '_constants/forms';
import { theme } from '_constants/theme';
import { getFieldProps } from '_helpers/forms/fields';
import { usePrevious } from '_hooks';

import Button from 'components/atomic/Button';
import Spinner from 'components/atomic/Spinner';
import BaseField from 'components/atomic/fields/BaseField';
import Text from 'components/atomic/layouts/Text';

const StyledMessageContainer = styled.div`
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.regular}px;
`;

const ChangePasswordForm = ({ loading, error, submitDisabled, defaultValues, onSubmit }) => {
  const {
    control,
    formState: { isSubmitted, isSubmitSuccessful, submitCount },
    reset,
    handleSubmit,
  } = useForm({
    defaultValues,
    reValidateMode: 'onChange',
  });

  const values = useWatch({ control });
  const prevLoading = usePrevious(loading);

  useEffect(() => {
    if (isSubmitted && !isSubmitSuccessful && submitCount) {
      onSubmit();
    }
  }, [isSubmitted, isSubmitSuccessful, submitCount, onSubmit]);

  useEffect(() => {
    if (prevLoading && !loading) {
      reset();
    }
  }, [prevLoading, loading]);

  return (
    <Form noValidate onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col xs={12}>
          <BaseField {...getFieldProps(FIELD_KEYS.oldPassword)} control={control} bottomSpace={theme.spaces.regular} />
        </Col>
        <Col xs={12}>
          <BaseField {...getFieldProps(FIELD_KEYS.newPassword)} control={control} bottomSpace={theme.spaces.regular} />
        </Col>
        <Col xs={12}>
          <BaseField
            {...getFieldProps(FIELD_KEYS.confirmation, {
              validationOptions: { values },
            })}
            control={control}
            bottomSpace={theme.spaces.regular}
          />
        </Col>
        <Col xs={12} md={3} />
        <Col xs={12} md={6}>
          <Button type="submit" disabled={submitDisabled} color={theme.colors.gray.primary}>
            Save New Password
          </Button>
        </Col>
        <Col xs={12} md={3} />
      </Row>

      {loading && (
        <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary} topSpace={theme.spaces.regular}>
          <StyledMessageContainer>
            <Spinner />
            <div>Updating...</div>
          </StyledMessageContainer>
        </Text>
      )}

      {error && (
        <Text size={theme.fontSizes.small} color={theme.colors.red.primary} topSpace={theme.spaces.regular}>
          {error?.message}
        </Text>
      )}
    </Form>
  );
};

ChangePasswordForm.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.object,
  submitDisabled: PropTypes.bool,
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
};

export default ChangePasswordForm;
